// ============================
// File: src/features/discovery/DiscoveryPlanner.jsx
// ============================
import React from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import { QueryClient, QueryClientProvider, useMutation } from "@tanstack/react-query";
import Modeler from "bpmn-js/lib/Modeler";
import { saveAs } from "file-saver";
import { runDiscoveryRecommendation } from "./api/discoveryClient";
import { updateDemandStage } from "../../api/demandsClient";
import { ConstraintsWizard, ApproachRecommendation } from "./components";
import { showToast, ToastContainer } from "../../components/Toast";
import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import "./discovery-bpmn.css";

const DEFAULT_BPMN_XML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" id="Definitions_1" targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="Process_1" isExecutable="false">
    <bpmn:startEvent id="StartEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="_BPMNShape_StartEvent_2" bpmnElement="StartEvent_1">
        <dc:Bounds x="173" y="102" width="36" height="36" />
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

// ---- Autosave helpers ----
const lsKey = (demandId) => `discovery:bpmn:${demandId}`;
const lsKeyLite = (demandId) => `discovery:bpmnLite:${demandId}`;
const debounce = (fn, ms = 800) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

function SavePill({ dirty, lastSavedAt }) {
  const when = lastSavedAt ? new Date(lastSavedAt).toLocaleTimeString() : null;
  if (dirty) {
    return <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs">Unsaved changes…</span>;
  }
  return <span className="rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs">Saved{when ? ` at ${when}` : ""}</span>;
}


function CanvasToolbar({ onZoomIn, onZoomOut, onFit }) {
  return (
<div className="relative top-2 right-2 z-20 flex gap-2">
    <button onClick={onZoomIn} className="rounded-lg bg-teal-600 text-white px-4 py-2 m-2 text-base font-bold hover:bg-teal-700 shadow-lg">🔍+</button>
     <button onClick={onZoomOut} className="rounded-lg bg-teal-600 text-white px-4 py-2 m-2 text-base font-bold hover:bg-teal-700 shadow-lg">🔍−</button>
      <button onClick={onFit} className="rounded-lg bg-teal-600 text-white px-4 py-2 m-2 text-base font-bold hover:bg-teal-700 shadow-lg">📐 Fit</button>
    </div>
  );
}

function useBpmnModeler(containerNode) {
  const modelerRef = useRef(null);

  useEffect(() => {
  if (!containerNode) return;
   const modeler = new Modeler({
    container: containerNode
   });
    modelerRef.current = modeler;
    (async () => {
      try {
        await modeler.importXML(DEFAULT_BPMN_XML);
        // fit to viewport on first load
        modeler.get("canvas").zoom("fit-viewport");
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("BPMN import error", e);
      }
    })();
    // handle window resizes
    const onResize = () => {
      try {
        modeler.get("canvas").resized();
      } catch {}
    };
    window.addEventListener("resize", onResize);
    return () => {
      modeler.destroy();
      modelerRef.current = null;
      window.removeEventListener("resize", onResize);
    };
  }, [containerNode]);

  return modelerRef;
}

function extractLiteJSON(modeler) {
  // Convert BPMN diagram to a minimal JSON (nodes + flows) for the AI backend
  const registry = modeler.get("elementRegistry");
  const elements = registry.getAll();
  const nodes = [];
  const flows = [];
  elements.forEach((el) => {
    if (!el.businessObject) return;
    const bo = el.businessObject;
    if (bo.$type?.includes("Flow")) {
      flows.push({ id: bo.id, sourceRef: bo.sourceRef?.id, targetRef: bo.targetRef?.id, type: bo.$type });
    } else if (
      bo.$type?.includes("Task") ||
      bo.$type?.includes("Event") ||
      bo.$type?.includes("Gateway")
    ) {
      nodes.push({ id: bo.id, name: bo.name || bo.id, type: bo.$type });
    }
  });
  return { nodes, flows };
}

function PlannerInner({ demandId, triageMeta = {} }) {
const [bpmnHost, setBpmnHost] = useState(null);
const modelerRef = useBpmnModeler(bpmnHost);
  const [constraints, setConstraints] = useState({
    timeline: "",
    budget: "",
    integrations: [],
    dataClass: "",
    notes: "",
  });
// 1) Save state / refs
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const importingRef = useRef(false); // prevents autosave loops while importing XML
  const [validationError, setValidationError] = useState("");

  // 2) Effect A — LOAD saved XML (or default) when modeler is ready
  useEffect(() => {
    const modeler = modelerRef.current;
    if (!modeler || !bpmnHost) return;
    (async () => {
      const saved = localStorage.getItem(lsKey(demandId));
      try {
        importingRef.current = true;
        await modeler.importXML(saved || DEFAULT_BPMN_XML);
        modeler.get("canvas").zoom("fit-viewport");
        setDirty(false);
        const ts = localStorage.getItem(lsKey(demandId) + ":ts");
        if (ts) setLastSavedAt(Number(ts));
      } finally {
        importingRef.current = false;
      }
    })();
    }, [demandId, modelerRef, bpmnHost]);

  // 3) Effect B — AUTOSAVE on edits (debounced)
    useEffect(() => {
        const modeler = modelerRef.current;
        if (!modeler) return;

        const doSave = debounce(async () => {
        if (importingRef.current) return;
        const { xml } = await modeler.saveXML({ format: true });
        const lite = extractLiteJSON(modeler);
        localStorage.setItem(lsKey(demandId), xml);
        localStorage.setItem(lsKeyLite(demandId), JSON.stringify(lite));
        const ts = Date.now();
        localStorage.setItem(lsKey(demandId) + ":ts", String(ts));
        setLastSavedAt(ts);
        setDirty(false);
        }, 800);

        const onChange = () => { if (!importingRef.current) { setDirty(true); doSave(); } };
        modeler.on("commandStack.changed", onChange);
        return () => { modeler.off("commandStack.changed", onChange); };
    }, [demandId, modelerRef]);

    const saveNow = async () => {
    const modeler = modelerRef.current;
    if (!modeler) return;
    try {
        const { xml } = await modeler.saveXML({ format: true });
        const lite = extractLiteJSON(modeler);
        localStorage.setItem(lsKey(demandId), xml);
        localStorage.setItem(lsKeyLite(demandId), JSON.stringify(lite));
        const ts = Date.now();
        localStorage.setItem(lsKey(demandId) + ":ts", String(ts));
        setLastSavedAt(ts);
        setDirty(false);
    } catch (e) {
        window.alert("Save failed");
    }
    };

    const loadSaved = async () => {
    const modeler = modelerRef.current;
    if (!modeler) return;
    const saved = localStorage.getItem(lsKey(demandId));
    if (!saved) { window.alert("No saved sketch found."); return; }
    try {
        importingRef.current = true;
        await modeler.importXML(saved);
        modeler.get("canvas").zoom("fit-viewport");
        setDirty(false);
    } finally { importingRef.current = false; }
    };

    const resetCanvas = async () => {
    const modeler = modelerRef.current;
    if (!modeler) return;
    if (!window.confirm("Reset canvas to a blank start event? This does not clear your saved version.")) return;
    try {
        importingRef.current = true;
        await modeler.importXML(DEFAULT_BPMN_XML);
        modeler.get("canvas").zoom("fit-viewport");
        setDirty(true);
    } finally { importingRef.current = false; }
    };

    const clearSaved = () => {
    localStorage.removeItem(lsKey(demandId));
    localStorage.removeItem(lsKey(demandId) + ":ts");
    localStorage.removeItem(lsKeyLite(demandId));
    setLastSavedAt(null);
    };


  const [isRunning, setIsRunning] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const recommendationMutation = useMutation({
   mutationFn: (payload) => runDiscoveryRecommendation(demandId, payload),
   onMutate: () => {
     setIsRunning(true);
   },
   onSuccess: () => {
     setIsRunning(false);
   },
   onError: () => {
     setIsRunning(false);
   }
  });

  const handleRun = async () => {
    setValidationError("");
    // Minimal validations per Acceptance Criteria
    if (!constraints.dataClass) {
      setValidationError("Select a data class (Public/PII/PHI/Controlled).");
      return;
    }
    const requiredAnswered = constraints.timeline && constraints.budget && constraints.integrations?.length >= 0;
    if (!requiredAnswered) {
      setValidationError("Please answer all constraint questions.");
      return;
    }

    const modeler = modelerRef.current;
    let bpmnXml = "";
    let bpmnLite = { nodes: [], flows: [] };
    try {
      const { xml } = await modeler.saveXML({ format: true });
      bpmnXml = xml;
      bpmnLite = extractLiteJSON(modeler);
    } catch (e) {
      setValidationError("Could not serialize BPMN diagram.");
      return;
    }

    const payload = {
      demandId,
      triageMeta,
      bpmnXml,
      bpmnLite,
      constraints,
    };

    recommendationMutation.mutate(payload);
  };

  const exportXml = async () => {
    try {
      const { xml } = await modelerRef.current.saveXML({ format: true });
      const blob = new Blob([xml], { type: "application/xml;charset=utf-8" });
      saveAs(blob, `${demandId}-discovery.bpmn`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert("Export failed");
    }
  };
  const zoom = (delta) => {
        const canvas = modelerRef.current?.get("canvas");
        if (!canvas) return;
        const current = canvas.zoom();
        canvas.zoom(Math.max(0.2, Math.min(4, current + delta)));
    };
  const fit = () => modelerRef.current?.get("canvas").zoom("fit-viewport");

  const loading = isRunning || recommendationMutation.isLoading;

  const handleCompleteDiscovery = async () => {
    try {
      // Determine next stage based on recommendation
      const recommendation = recommendationMutation.data;
      const needsPOC = recommendation?.discovery_path === 'POC' ||
                       (recommendation?.confidence && recommendation.confidence < 0.7);

      // 'POC' and 'REQUIREMENTS' are the values the backend writes and every stage
      // filter matches on. This used to send 'ENHANCED_DISCOVERY' / 'AI_REQUIREMENTS',
      // which no filter recognised — so completing Discovery made the demand vanish
      // from the very screens it was being handed to.
      const nextStage = needsPOC ? 'POC' : 'REQUIREMENTS';
      const nextStageName = needsPOC ? 'Enhanced Discovery (POC)' : 'AI Requirements';
      // ...and this pointed at /aieaai/enhanced-discovery/, which is not a route.
      const nextUrl = needsPOC ? `/aieaai/poc/${demandId}` : `/aieaai/requirements/${demandId}`;

      await updateDemandStage(demandId, nextStage, 'IN_PROGRESS');
      setShowCompleteModal(false);
      showToast(`Discovery completed! Moving to ${nextStageName}...`, 'success');
      setTimeout(() => {
        window.location.href = nextUrl;
      }, 1500);
    } catch (error) {
      showToast('Failed to update stage: ' + (error.message || error), 'error');
    }
  };

  return (
    <>
      <ToastContainer />
      <div style={{ padding: '24px' }}>
        {/* Loading Overlay */}
        {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 shadow-2xl flex flex-col items-center gap-4">
            <svg className="animate-spin h-12 w-12 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <div className="text-center">
              <h3 className="text-lg font-bold text-gray-900">Analyzing Your Process...</h3>
              <p className="text-sm text-gray-600 mt-1">AI is evaluating constraints and BPMN diagram</p>
              <p className="text-xs text-gray-500 mt-2">This may take 5-15 seconds</p>
            </div>
          </div>
        </div>
      )}

      {/* Complete Discovery Confirmation Modal */}
      {showCompleteModal && (() => {
        const recommendation = recommendationMutation.data;
        const needsPOC = recommendation?.discovery_path === 'POC' ||
                         (recommendation?.confidence && recommendation.confidence < 0.7);
        const nextStageName = needsPOC ? 'Enhanced Discovery (POC)' : 'AI Requirements';
        const nextStageDescription = needsPOC
          ? 'run a Proof of Concept to validate the approach'
          : 'generate detailed requirements using AI';

        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                  <span className="text-3xl">✅</span>
                  Complete Discovery Phase
                </h3>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                <p className="text-gray-700 text-lg">
                  Are you ready to complete the Discovery phase and move to <strong className="text-purple-600">{nextStageName}</strong>?
                </p>

                <div className="bg-purple-50 border-l-4 border-purple-600 p-4 rounded">
                  <h4 className="font-bold text-purple-900 mb-2">What happens next:</h4>
                  <ul className="space-y-2 text-sm text-purple-800">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold">1.</span>
                      <span>Discovery phase will be marked as <strong>COMPLETE</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold">2.</span>
                      <span>Demand stage will advance to <strong>{nextStageName}</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 font-bold">3.</span>
                      <span>You'll be redirected to {nextStageDescription}</span>
                    </li>
                  </ul>
                </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Note:</strong> Make sure you've saved your BPMN diagram and reviewed the recommendation before proceeding.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowCompleteModal(false)}
                className="px-6 py-2.5 rounded-lg border-2 border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCompleteDiscovery}
                style={{ backgroundColor: 'var(--cares-purple)', color: 'var(--cares-text-max)' }}
                className="px-6 py-2.5 rounded-lg font-bold shadow-lg hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <span>✅</span>
                <span>Yes, Complete Discovery</span>
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Demand Info Card */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        border: '1px solid var(--cares-line-card)',
        marginBottom: '24px'
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
          📋 Demand Information
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <div>
            <span style={{ fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '600' }}>ID:</span>
            <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '500', marginTop: '4px' }}>{demandId}</p>
          </div>
          <div>
            <span style={{ fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '600' }}>Title:</span>
            <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '500', marginTop: '4px' }}>{triageMeta?.title || "—"}</p>
          </div>
          <div>
            <span style={{ fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '600' }}>Owner:</span>
            <p style={{ fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '500', marginTop: '4px' }}>{triageMeta?.businessOwner || "—"}</p>
          </div>
        </div>
      </div>

      {/* Constraints Card */}
      <ConstraintsWizard value={constraints} onChange={setConstraints} />

      {/* BPMN Designer */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        border: '1px solid var(--cares-line-card)',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--cares-text-1)' }}>
            🎨 Process Designer
          </h2>
          <SavePill dirty={dirty} lastSavedAt={lastSavedAt} />
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button onClick={saveNow} style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            background: 'var(--cares-purple)',
            color: 'var(--cares-text-max)',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}>
            💾 Save
          </button>
          <button onClick={loadSaved} style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            background: 'var(--cares-teal)',
            color: 'var(--cares-text-max)',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}>
            📂 Load
          </button>
          <button onClick={resetCanvas} style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            background: 'var(--cares-orange)',
            color: 'var(--cares-text-max)',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}>
            🔄 Reset
          </button>
          <button onClick={clearSaved} style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            background: 'var(--cares-orange)',
            color: 'var(--cares-text-max)',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}>
            🗑️ Clear
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button onClick={() => zoom(0.2)} style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--cares-teal)',
              color: 'var(--cares-text-max)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}>
              🔍+
            </button>
            <button onClick={() => zoom(-0.2)} style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--cares-teal)',
              color: 'var(--cares-text-max)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}>
              🔍−
            </button>
            <button onClick={fit} style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--cares-teal)',
              color: 'var(--cares-text-max)',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}>
              📐 Fit
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div style={{
          border: '1px solid var(--cares-line-card)',
          borderRadius: '8px',
          overflow: 'hidden',
          background: 'var(--cares-surface-board)'
        }}>
          <div
            id="bpmn-canvas"
            ref={setBpmnHost}
            style={{ height: 500 }}
          />
        </div>
      </div>

      {/* Actions */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        border: '1px solid var(--cares-line-card)',
        marginBottom: '24px'
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
          ⚡ Actions
        </h2>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleRun}
            disabled={loading}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              background: loading ? 'var(--cares-surface-raised)' : 'var(--cares-green)',
              color: 'var(--cares-text-max)',
              fontSize: '15px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Running AI Analysis...</span>
              </>
            ) : (
              <>
                <span>🤖</span>
                <span>Run AI Recommendation</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={exportXml}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--cares-blue)',
              color: 'var(--cares-text-max)',
              fontSize: '15px',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            💾 Export BPMN
          </button>

          {/* Show "Complete Discovery" button only after recommendation is received */}
          {recommendationMutation.data && (
            <button
              type="button"
              onClick={() => setShowCompleteModal(true)}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--cares-purple)',
                color: 'var(--cares-text-max)',
                fontSize: '15px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              ✅ Complete Discovery
            </button>
          )}
        </div>
        {validationError && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            borderRadius: '8px',
            background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
            border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
            color: 'var(--cares-orange)',
            fontSize: '14px'
          }}>
            {validationError}
          </div>
        )}
      </div>

      {/* Recommendation */}
      <ApproachRecommendation result={recommendationMutation.data} isLoading={loading} error={recommendationMutation.error} />
      </div>
    </>
  );
}

export default function DiscoveryPlanner({ demandId, triageMeta = {} }) {
  const client = useMemo(() => new QueryClient(), []);
  return (
    <QueryClientProvider client={client}>
      <PlannerInner demandId={demandId} triageMeta={triageMeta} />
    </QueryClientProvider>
  );
}